import { memo } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import dayjs from 'dayjs'

import Section from '~/components/Section'
import type { GiftWithPurchaseControl } from '../hooks'

type Props = {
    control: GiftWithPurchaseControl
}

export default memo(function CampaignInfo({ control }: Props) {
    const { t } = useTranslation('gift_with_purchase')
    const id = useWatch({ control, name: 'id' })
    const startDate = useWatch({ control, name: 'start_date' })
    const endDate = useWatch({ control, name: 'end_date' })

    return (
        <Section
            component={Stack}
            gap={2}
            header={
                <Section.Header
                    label={t('form.campaign_info.title')}
                    actions={
                        id ? (
                            <Controller
                                control={control}
                                name="is_active"
                                render={({ field }) => (
                                    <FormControlLabel
                                        {...field}
                                        checked={field.value}
                                        control={<Switch color="secondary" />}
                                        label={t('form.campaign_info.field_active')}
                                        labelPlacement="start"
                                    />
                                )}
                            />
                        ) : null
                    }
                />
            }
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" gap={1}>
                    <Controller
                        control={control}
                        name="name"
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                size="small"
                                color="secondary"
                                label={t('form.campaign_info.field_name')}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="start_date"
                        render={({ field, fieldState }) => (
                            <DateTimePicker
                                {...field}
                                disablePast
                                shouldDisableDate={(date) => {
                                    return endDate ? date > dayjs(endDate) : false
                                }}
                                value={field.value ? dayjs(field.value) : null}
                                label={t('form.campaign_info.field_start_date')}
                                slotProps={{
                                    textField: {
                                        color: 'secondary',
                                        size: 'small',
                                        error: !!fieldState.error,
                                        helperText: fieldState.error?.message,
                                    },
                                }}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="end_date"
                        render={({ field, fieldState }) => (
                            <DateTimePicker
                                {...field}
                                disablePast
                                shouldDisableTime={(date) => {
                                    return startDate ? date < dayjs(startDate) : false
                                }}
                                shouldDisableDate={(date) => {
                                    return startDate ? date < dayjs(startDate) : false
                                }}
                                value={field.value ? dayjs(field.value) : null}
                                label={t('form.campaign_info.field_end_date')}
                                slotProps={{
                                    textField: {
                                        color: 'secondary',
                                        size: 'small',
                                        error: !!fieldState.error,
                                        helperText: fieldState.error?.message,
                                    },
                                }}
                            />
                        )}
                    />
                </Stack>
            </Stack>

            <div>
                <Controller
                    control={control}
                    name="can_be_combined"
                    render={({ field }) => (
                        <FormControlLabel
                            {...field}
                            control={<Checkbox color="secondary" />}
                            label={t('form.campaign_info.field_can_be_combined')}
                            labelPlacement="start"
                            style={{ marginLeft: 0 }}
                        />
                    )}
                />
            </div>
        </Section>
    )
})
